import { useEffect, useState } from 'react';

import withBuilderDefaults, {
  isBlank,
  keepCompleteItems,
} from '../../../builder-io/utils/withBuilderDefaults';
import { fetchBuilderDataFromModel } from '../../../common/utils';

export interface SearchHeroBuilderData {
  headingFirstLine: string;
  headingSecondLine: string;
  videoSrc: string;
  imageSrc: string;
  trendingTags: { searchTerm: string }[];
}

const BUILDER_IMAGE_BASE =
  'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F';

const toTags = (terms: string[]): { searchTerm: string }[] =>
  terms.map((searchTerm) => ({ searchTerm }));

// Design content, used for whatever the Builder entry leaves empty. Keyed by
// slug because every landing page has its own copy, artwork and search terms.
export const SEARCH_HERO_DEFAULTS: Record<string, SearchHeroBuilderData> = {
  '/video': {
    headingFirstLine: 'Get unlimited high quality stock video',
    headingSecondLine:
      'Download royalty-free stock video in HD and 4K for any creative project',
    imageSrc: `${BUILDER_IMAGE_BASE}dcac348bd3c044709eee059c4e714d11`,
    videoSrc:
      'https://cdn.builder.io/o/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F93eeed6ea6fc42008c71acfa42b87e11?alt=media&token=b0ca8fa9-09b3-4c5b-91b7-c59d9951ea78&apiKey=e7fb3d1e4da14573bd2a1edb7bfee5f1',
    trendingTags: toTags([
      'Portrait',
      'Money',
      'Office',
      'Nature',
      'Technology',
      'AI',
    ]),
  },
  '/audio': {
    headingFirstLine:
      '100% exclusive music library that you can only find here',
    headingSecondLine:
      'Create content that stands out with unlimited access to 33,000+ royalty free music tracks from award-winning artists.',
    imageSrc: `${BUILDER_IMAGE_BASE}25a8a12d19b045279bd6ee9a832b8e15`,
    videoSrc: '',
    trendingTags: toTags([
      'Happy',
      'Playful',
      'Blues',
      'Ambient',
      'Technology',
      'Corporate',
    ]),
  },
  '/images': {
    headingFirstLine: 'Unlimited stock photos, vectors, & illustrations',
    headingSecondLine:
      'Download royalty-free stock images in HD and 4K for any creative project',
    imageSrc: `${BUILDER_IMAGE_BASE}0254f31d1dcc40aba7d048a3f06393b0`,
    videoSrc: '',
    trendingTags: toTags(['Environment', 'Background', 'Office', 'Stars']),
  },
};

/**
 * Fills whatever the Builder entry left empty with the design content for that
 * slug. Returns null only for a slug we have no design content for, which is
 * the caller's signal to render the standard search page instead.
 */
export const resolveSearchHeroContent = (
  data: Partial<SearchHeroBuilderData> | null,
  urlSlug: string
): SearchHeroBuilderData | null => {
  const defaults = SEARCH_HERO_DEFAULTS[urlSlug];
  if (!defaults) return null;

  // The two heading lines are one sentence split in two, so they fall back as a
  // pair: pairing a Builder line with a default one can read as nonsense.
  const hasBothHeadings =
    !isBlank(data?.headingFirstLine) && !isBlank(data?.headingSecondLine);

  return withBuilderDefaults(
    {
      headingFirstLine: hasBothHeadings ? data.headingFirstLine : '',
      headingSecondLine: hasBothHeadings ? data.headingSecondLine : '',
      imageSrc: data?.imageSrc,
      videoSrc: data?.videoSrc,
      trendingTags: keepCompleteItems(data?.trendingTags, ['searchTerm']),
    },
    defaults
  );
};

// Fetches the landing-search-page hero data for a given urlSlug and merges it
// with the design defaults. `enabled=false` skips the request and reports
// isLoading=false immediately.
export const useSearchHeroData = (urlSlug: string, enabled = true) => {
  const [builderData, setBuilderData] =
    useState<Partial<SearchHeroBuilderData> | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchBuilderData = async () => {
      const data = await fetchBuilderDataFromModel('landing-search-page', {
        query: { 'data.urlSlug': urlSlug },
        options: { fields: 'data' },
      });
      if (data && data.searchHero) {
        setBuilderData({
          headingFirstLine: data.searchHero.headingFirstLine,
          headingSecondLine: data.searchHero.headingSecondLine,
          videoSrc: data.searchHero.videoSrc,
          imageSrc: data.searchHero.imageSrc,
          trendingTags: data.searchHero.trendingTags,
        });
      }
    };
    fetchBuilderData().finally(() => setIsLoading(false));
  }, [urlSlug, enabled]);

  return {
    builderData: enabled
      ? resolveSearchHeroContent(builderData, urlSlug)
      : null,
    isLoading,
  };
};
